import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  FormGroup,
  HelperText,
  HelperTextItem,
  Menu,
  MenuContent,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Popover,
  Popper,
  Radio,
  SearchInput,
  TextInput,
} from '@patternfly/react-core';
import { OutlinedQuestionCircleIcon } from '@patternfly/react-icons';
import { useTimeTracker } from '../../context/TimeTrackerContext';
import { useKeycloak } from '../KeycloakProvider';
import {
  formatDateISO,
  formatTimeInput,
  formatTimeSpent,
  parseDurationString,
  parseTimeInput,
} from '../../utils';
import { buildFilterString } from '../../hooks/useIssuesData';
import { useTimeCalculation } from '../../hooks/useTimeCalculation';
import { Issue } from '../../types';
import { useAvailableLabels } from '../../hooks';
import { HAS_API } from '../../config';
import { LabelSelect, SegmentedTimeInput } from '../common';
import './CreateIssueModal.css';

type EditMode = 'duration' | 'timeRange';

interface CreateIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateIssueModal: React.FC<CreateIssueModalProps> = ({ isOpen, onClose }) => {
  const { createWorkLog, fetchIssues, currentUser } = useTimeTracker();
  const { keycloak } = useKeycloak();
  const { labels: availableLabels } = useAvailableLabels();

  // Check if user is admin
  const isAdmin = useMemo(() => {
    const realmRoles = keycloak?.tokenParsed?.realm_access?.roles || [];
    return realmRoles.includes('ROLE_ADMIN');
  }, [keycloak?.tokenParsed?.realm_access?.roles]);

  // Issue search state
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<Issue[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);

  // Time input state
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [durationInput, setDurationInput] = useState('');
  const [editMode, setEditMode] = useState<EditMode>('duration');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Labels state
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  // Refs for Popper positioning
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const resetForm = useCallback(() => {
    setSearchValue('');
    setSearchResults([]);
    setSelectedIssue(null);
    setIsMenuOpen(false);
    setDate('');
    setStartTime('');
    setEndTime('');
    setDurationInput('');
    setEditMode('duration');
    setSelectedLabels([]);
    setError(null);
  }, []);

  // Initialize form with default values when modal opens
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      setDate(formatDateISO(now));
      setStartTime(formatTimeInput(now));
    }
  }, [isOpen]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Search for issues with debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    setSelectedIssue(null);
    setError(null);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setIsMenuOpen(false);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Admin can search all issues, regular user only their assigned issues
        const assigneeId = isAdmin ? undefined : currentUser?.id;
        const filter = buildFilterString(value, [], [], assigneeId);
        const result = await fetchIssues({
          pageSize: 10,
          filter,
          orderBy: 'updatedAt desc',
        });
        setSearchResults(result.items);
        setIsMenuOpen(result.items.length > 0);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [currentUser?.id, fetchIssues, isAdmin]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleIssueSelect = useCallback((issue: Issue) => {
    setSelectedIssue(issue);
    setSearchValue(`${issue.key}: ${issue.summary}`);
    setIsMenuOpen(false);
    setSearchResults([]);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIssue(null);
    setSearchValue('');
    setSearchResults([]);
    setIsMenuOpen(false);
    searchInputRef.current?.focus();
  }, []);

  const handleEndTimeAutoFill = useCallback((currentValue: string): string | undefined => {
    if (!currentValue && startTime) {
      const [h, m] = startTime.split(':').map(Number);
      return formatTimeInput(new Date(0, 0, 0, h, m + 15));
    }
    return undefined;
  }, [startTime]);

  // ── Calculations ──

  const { calculatedEndTime, calculatedDuration } = useTimeCalculation(editMode, date, startTime, endTime, durationInput);

  const handleModeChange = (mode: EditMode) => {
    setEditMode(mode);
    setError(null);
    if (mode === 'timeRange') {
      setEndTime('');
    } else if (mode === 'duration') {
      setDurationInput(calculatedDuration && !calculatedDuration.error
        ? formatTimeSpent(calculatedDuration.seconds)
        : '');
    }
  };

  const validate = (): string | null => {
    if (!selectedIssue) return 'Please select a ticket.';
    if (!date) return 'Date is required.';
    if (!startTime) return 'Start time is required.';

    if (editMode === 'duration') {
      if (!durationInput) return 'Duration is required.';
      const parsed = parseDurationString(durationInput);
      if (parsed === null || parsed <= 0) return 'Enter a valid duration (e.g., "2h 30m", "90m", "1.5h").';
    }
    if (editMode === 'timeRange') {
      if (!endTime) return 'End time is required.';
      if (calculatedDuration?.error) return 'End time must be after start time.';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const baseDate = new Date(date);
      const startDateTime = parseTimeInput(startTime, '0', baseDate);

      let endDateTime: Date;
      if (editMode === 'duration') {
        const durationSeconds = parseDurationString(durationInput)!;
        endDateTime = new Date(startDateTime.getTime() + durationSeconds * 1000);
      } else {
        endDateTime = parseTimeInput(endTime, '0', baseDate);
      }

      await createWorkLog(selectedIssue!.id, selectedIssue!.key, startDateTime, endDateTime, HAS_API && selectedLabels.length > 0 ? selectedLabels : undefined);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log time. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = !validate();

  // Menu for search results
  const searchMenu = (
    <Menu ref={menuRef} onSelect={() => {}} isScrollable>
      <MenuContent maxMenuHeight="250px">
        <MenuList>
          {searchResults.map((issue) => (
            <MenuItem
              key={issue.id}
              onClick={() => handleIssueSelect(issue)}
              description={issue.summary}
            >
              <strong>{issue.key}</strong>
            </MenuItem>
          ))}
        </MenuList>
      </MenuContent>
    </Menu>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      aria-labelledby="create-issue-modal-title"
      aria-describedby="create-issue-modal-body"
      variant="small"
    >
      <ModalHeader title="Quick log" labelId="create-issue-modal-title" />
      <ModalBody id="create-issue-modal-body">
        <Form>
          {/* Issue search */}
          <FormGroup
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Ticket
                <Popover
                  headerContent="Search tips"
                  minWidth="490px"
                  bodyContent={
                    <div style={{ fontSize: 'var(--pf-t--global--font--size--sm)', lineHeight: 1.8, padding: '4px 0' }}>
                      <p style={{ marginBottom: 12 }}>Type any text to search across ticket key and summary.</p>
                      <p style={{ marginBottom: 8, fontWeight: 600 }}>Advanced filters with <code style={{ background: 'var(--pf-t--global--background--color--secondary--default)', padding: '1px 4px', borderRadius: 3 }}>*</code> wildcard:</p>
                      <table style={{ width: '100%', fontSize: 'var(--pf-t--global--font--size--xs)', borderSpacing: '0 8px' }}>
                        <tbody>
                          <tr>
                            <td style={{ paddingRight: 12 }}><code style={{ background: 'var(--pf-t--global--background--color--secondary--default)', padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap' }}>key=&quot;PROJ-*&quot;</code></td>
                            <td style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>keys starting with PROJ-</td>
                          </tr>
                          <tr>
                            <td style={{ paddingRight: 12 }}><code style={{ background: 'var(--pf-t--global--background--color--secondary--default)', padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap' }}>summary=&quot;*login*&quot;</code></td>
                            <td style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>summary containing login</td>
                          </tr>
                          <tr>
                            <td style={{ paddingRight: 12 }}><code style={{ background: 'var(--pf-t--global--background--color--secondary--default)', padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap' }}>status=&quot;Open&quot;</code></td>
                            <td style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>filter by status</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  }
                  position="right"
                >
                  <button
                    type="button"
                    aria-label="Search help"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'var(--pf-t--global--icon--color--subtle)', display: 'inline-flex' }}
                  >
                    <OutlinedQuestionCircleIcon style={{ fontSize: '0.85em' }} />
                  </button>
                </Popover>
              </span>
            }
            isRequired
            fieldId="quick-log-search"
          >
            <Popper
              trigger={
                <SearchInput
                  ref={searchInputRef}
                  id="quick-log-search"
                  value={searchValue}
                  onChange={(_event, value) => handleSearchChange(value)}
                  onClear={() => handleClearSelection()}
                  placeholder="Search by ticket key or summary..."
                  aria-label="Search tickets"
                  isDisabled={isSubmitting}
                  resultsCount={isSearching ? undefined : (isMenuOpen ? searchResults.length : undefined)}
                />
              }
              popper={searchMenu}
              isVisible={isMenuOpen && searchResults.length > 0}
              appendTo={() => document.body}
              enableFlip
              zIndex={9999}
            />
            {selectedIssue && (
              <HelperText>
                <HelperTextItem variant="success">
                  Selected: {selectedIssue.key}
                </HelperTextItem>
              </HelperText>
            )}
          </FormGroup>

          {/* Labels */}
          {HAS_API && (
            <FormGroup label="Labels" fieldId="quick-log-labels">
              <LabelSelect
                availableLabels={availableLabels}
                selectedLabels={selectedLabels}
                onSelectionChange={setSelectedLabels}
                isDisabled={isSubmitting}
                labelGroupClassName="quick-log-selected-labels"
              />
            </FormGroup>
          )}

          {/* Date */}
          <FormGroup label="Date" isRequired fieldId="quick-log-date">
            <DatePicker
              id="quick-log-date"
              value={date}
              onChange={(_event, value) => setDate(value)}
              aria-label="Select date"
              appendTo={() => document.body}
            />
          </FormGroup>

          {/* Mode selector */}
          <div className="create-issue-mode-selector">
            <Radio
              isChecked={editMode === 'duration'}
              name="quick-log-mode"
              onChange={() => handleModeChange('duration')}
              label="Duration"
              id="quick-log-mode-duration"
            />
            <Radio
              isChecked={editMode === 'timeRange'}
              name="quick-log-mode"
              onChange={() => handleModeChange('timeRange')}
              label="Time range"
              id="quick-log-mode-timerange"
            />
          </div>

          {/* Duration mode */}
          {editMode === 'duration' && (
            <div className="create-issue-duration-mode">
              <FormGroup label="Start time" isRequired fieldId="quick-log-start-time">
                <SegmentedTimeInput
                  id="quick-log-start-time"
                  value={startTime}
                  onChange={setStartTime}
                  ariaLabel="Start time"
                />
              </FormGroup>

              <FormGroup label="Duration" isRequired fieldId="quick-log-duration">
                <TextInput
                  type="text"
                  id="quick-log-duration"
                  value={durationInput}
                  onChange={(_event, value) => setDurationInput(value)}
                  placeholder="e.g., 2h 30m"
                  aria-label="Duration"
                />
                <HelperText>
                  <HelperTextItem variant="default">
                    Format: 2h 30m, 90m, or 1.5h
                  </HelperTextItem>
                </HelperText>
              </FormGroup>

              {calculatedEndTime && (
                <div className="create-issue-calculated-info">
                  End time: {calculatedEndTime}
                </div>
              )}
            </div>
          )}

          {/* Time range mode */}
          {editMode === 'timeRange' && (
            <div className="create-issue-timerange-mode">
              <div className="create-issue-time-row">
                <FormGroup label="Start time" isRequired fieldId="quick-log-start-time-range">
                  <SegmentedTimeInput
                    id="quick-log-start-time-range"
                    value={startTime}
                    onChange={setStartTime}
                    ariaLabel="Start time"
                  />
                </FormGroup>

                <FormGroup label="End time" isRequired fieldId="quick-log-end-time">
                  <SegmentedTimeInput
                    id="quick-log-end-time"
                    value={endTime}
                    onChange={setEndTime}
                    ariaLabel="End time"
                    onFocus={handleEndTimeAutoFill}
                  />
                </FormGroup>
              </div>

              {calculatedDuration && (
                <div className={`create-issue-calculated-info ${calculatedDuration.error ? 'create-issue-calculated-info--error' : ''}`}>
                  {calculatedDuration.error
                    ? 'End time must be after start time'
                    : `Duration: ${formatTimeSpent(calculatedDuration.seconds)}`}
                </div>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <HelperText>
              <HelperTextItem variant="error">{error}</HelperTextItem>
            </HelperText>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          key="submit"
          variant="primary"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={isSubmitting || !isValid}
        >
          Save
        </Button>
        <Button key="cancel" variant="link" onClick={handleClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
